import moment from 'moment';
import _ from 'lodash';

// 01/01/2001 @ 12:00am (UTC)
// We choose this date because it starts on a monday and therefore fits well with the timeline render
export const TIMELINE_START_DATE = '2001-01-01 00:00:00';
export const TIMELINE_DAYS_TO_SHOW = 120;

export function templateToTimeline(template) {
  const items = [];
  const groups = [];
  template.activities.forEach((a) => {
    const start = a.start.indexOf(' ') === -1 ? `0 ${a.start}` : a.start;
    const end = a.end.indexOf(' ') === -1 ? `0 ${a.end}` : a.end;

    const item = {
      id: a.id,
      start_time: moment(TIMELINE_START_DATE)
        .add(moment.duration(start, 'DD H:m:s')),
      end_time: moment(TIMELINE_START_DATE)
        .add(moment.duration(end, 'DD H:m:s')),
      group: a.placeholder_person_id - 1, // -1 to start placeholders at 0
      title: a.activity.name,
      color: a.activity.color,
      activity: {
        ...a.activity,
        duration: moment.duration(a.activity.duration)
          .asMinutes()
      },
      is_new: false,
      default_notifications: a.default_notifications
    };

    groups.push(item.group);
    items.push(item);
  });

  const newGroups = [...new Set(groups)].map(g => ({
    id: g,
    title: 'User'
  }));

  newGroups.sort((a, b) => a.id - b.id);

  return {
    items,
    groups: newGroups,
    template
  };
}

export function timelineToTemplate(items) {
  const timelineStartDateOffset = moment(TIMELINE_START_DATE);

  return items.map((i) => {
    // start and end times are in Unix(ms) time.
    // Each of these needs to be converted to a duration from a common start point.
    const sDuration = moment.duration(i.start_time.valueOf() - timelineStartDateOffset.valueOf());
    const eDuration = moment.duration(i.end_time.valueOf() - timelineStartDateOffset.valueOf());

    const sDurationStr = `${Math.trunc(sDuration.asDays())} ${sDuration.hours()}:${sDuration.minutes()}:${sDuration.seconds()}.${sDuration.milliseconds()}`;
    const eDurationStr = `${Math.trunc(eDuration.asDays())} ${eDuration.hours()}:${eDuration.minutes()}:${eDuration.seconds()}.${eDuration.milliseconds()}`;

    // Increment group, as groups are 0-indexed while placeholder users are 1-indexed
    return {
      id: i.id,
      is_new: 'is_new' in i ? i.is_new : true,
      start: sDurationStr,
      end: eDurationStr,
      placeholder_id: i.group + 1,
      activity_id: i.activity.id
    };
  });
}

export function assignTemplateItemsToUsers(templateItems, templateGroups, startTime, isRelative, unallocateUnassigned) {
  const timelineStartDateOffset = moment(TIMELINE_START_DATE);
  let assignedItems = [];
  // Cycle through each group and re-assign the group field to match the user id rather than the place holder
  // Any items without an assigned user are ignored.
  // Calculate the start time/end time of the activity based on the difference from our constant template start time

  // Absolute time == relative time with a midnight start
  if (!isRelative) {
    startTime.startOf('day');
  }

  templateGroups.forEach((g) => {
    if (g.assignedUser || unallocateUnassigned) {
      // Have to deepclone this filter, otherwise manipulating the start/end/group fields will actually change
      // the tempateItems array via the same object references.
      const groupItems = _.cloneDeep(
        templateItems.filter(i => i.group === g.id)
      );
      for (let i = 0; i < groupItems.length; i++) {
        const gi = groupItems[i];
        gi.start_time = startTime.clone()
          .add(gi.start_time.valueOf() - timelineStartDateOffset.valueOf());
        gi.end_time = startTime.clone()
          .add(gi.end_time.valueOf() - timelineStartDateOffset.valueOf());
        gi.user_id = g.assignedUser ? g.assignedUser.uid : -1; // -1 = unallocated user
        gi.user = g.assignedUser;
      }

      assignedItems = assignedItems.concat(groupItems);
    }
  });

  return assignedItems;
}

export function rosterBuilderToTimeline(rosterBuilder) {
  const items = [];
  const groups = [];
  rosterBuilder.activities.forEach((a) => {
    const start = a.start.indexOf(' ') === -1 ? `0 ${a.start}` : a.start;
    const end = a.end.indexOf(' ') === -1 ? `0 ${a.end}` : a.end;
    const item = {
      id: a.id,
      start_time: moment(TIMELINE_START_DATE)
        .add(moment.duration(start, 'DD H:m:s')),
      end_time: moment(TIMELINE_START_DATE)
        .add(moment.duration(end, 'DD H:m:s')),
      group: a.placeholder_person_id - 1, // -1 to start placeholders at 0
      title: a.activity.name,
      color: a.activity.color,
      activity: {
        ...a.activity,
        duration: moment.duration(a.activity.duration)
          .asMinutes()
      },
      is_new: false,
      default_notifications: a.default_notifications,
      user: a.user
    };

    groups.push(item.group);
    items.push(item);
  });

  const newGroups = [...new Set(groups)].map(g => ({
    id: g,
    title: 'User'
  }));

  newGroups.sort((a, b) => a.id - b.id);

  return {
    items,
    groups: newGroups,
    rosterBuilder
  };
}

export function timelineToRosterBuilder(items, groups) {
  const timelineStartDateOffset = moment(TIMELINE_START_DATE);

  return items.map((i) => {
    // start and end times are in Unix(ms) time.
    // Each of these needs to be converted to a duration from a common start point.
    const sDuration = moment.duration(i.start_time.valueOf() - timelineStartDateOffset.valueOf());
    const eDuration = moment.duration(i.end_time.valueOf() - timelineStartDateOffset.valueOf());

    const sDurationStr = `${Math.trunc(sDuration.asDays())} ${sDuration.hours()}:${sDuration.minutes()}:${sDuration.seconds()}.${sDuration.milliseconds()}`;
    const eDurationStr = `${Math.trunc(eDuration.asDays())} ${eDuration.hours()}:${eDuration.minutes()}:${eDuration.seconds()}.${eDuration.milliseconds()}`;
    const user_id = groups.map((grp) => {
      if (grp.id === i.group) {
        return grp?.assignedUser?.uid;
      }
      return null;
    });

    // Increment group, as groups are 0-indexed while placeholder users are 1-indexed
    return {
      id: i.id,
      is_new: 'is_new' in i ? i.is_new : true,
      start: sDurationStr,
      end: eDurationStr,
      placeholder_id: i.group + 1,
      activity_id: i.activity.id,
      user: user_id[i.group] || null
    };
  });
}

export function assignRosterBuilderItemsToUsers(rosterBuilderItems, rosterBuilderGroups, startTime, isRelative, unallocateUnassigned) {
  const timelineStartDateOffset = moment(TIMELINE_START_DATE);
  let assignedItems = [];
  // Cycle through each group and re-assign the group field to match the user id rather than the place holder
  // Any items without an assigned user are ignored.
  // Calculate the start time/end time of the activity based on the difference from our constant template start time

  // Absolute time == relative time with a midnight start
  if (!isRelative) {
    startTime.startOf('day');
  }

  rosterBuilderGroups.forEach((g) => {
    if (g.assignedUser || unallocateUnassigned) {
      // Have to deepclone this filter, otherwise manipulating the start/end/group fields will actually change
      // the tempateItems array via the same object references.
      const groupItems = _.cloneDeep(
        rosterBuilderItems.filter(i => i.group === g.id)
      );
      for (let i = 0; i < groupItems.length; i++) {
        const gi = groupItems[i];
        gi.start_time = startTime.clone()
          .add(gi.start_time.valueOf() - timelineStartDateOffset.valueOf());
        gi.end_time = startTime.clone()
          .add(gi.end_time.valueOf() - timelineStartDateOffset.valueOf());
        gi.user_id = g.assignedUser ? g.assignedUser.uid : -1; // -1 = unallocated user
        gi.user = g.assignedUser;
      }

      assignedItems = assignedItems.concat(groupItems);
    }
  });

  return assignedItems;
}
